"use client";

import { useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
gsap.registerPlugin(useGSAP, ScrollTrigger);
export default function Consultation() {
  const container = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: container.current,
          start: "top 70%",
          once: true
        },
        defaults: {
          ease: "power3.out"
        }
      });
      tl.from(".legacy-kicker", {
        opacity: 0,
        y: 16,
        duration: 0.6
      }).from(".legacy-rule", {
        scaleX: 0,
        transformOrigin: "center",
        duration: 0.5
      }, "-=0.3").from(".legacy-line", {
        yPercent: 110,
        opacity: 0,
        duration: 0.9,
        stagger: 0.09,
        ease: "cubic-bezier(0.2, 0.7, 0.15, 1)"
      }, "-=0.1").from(".legacy-marker path", {
        strokeDashoffset: (i, el) => (el as SVGPathElement).getTotalLength(),
        duration: 0.62,
        ease: "power2.out"
      }, "-=0.2").from(".legacy-lede", {
        opacity: 0,
        y: 20,
        duration: 0.7
      }, "-=0.3").from(".legacy-cta-wrap", {
        opacity: 0,
        y: 20,
        duration: 0.7
      }, "-=0.4").from(".legacy-sticker", {
        opacity: 0,
        scale: 0.7,
        rotate: -20,
        duration: 0.6,
        ease: "back.out(2)"
      }, "-=0.4").to(".legacy-sticker", {
        rotate: -4,
        duration: 0.35,
        ease: "sine.inOut",
        yoyo: true,
        repeat: 1
      }).from(".legacy-tertiary", {
        opacity: 0,
        y: 12,
        duration: 0.5
      }, "-=0.5");
      const paths = container.current?.querySelectorAll(".legacy-marker path");
      paths?.forEach(p => {
        const path = p as SVGPathElement;
        const len = path.getTotalLength();
        path.style.strokeDasharray = `${len}`;
        path.style.strokeDashoffset = "0";
      });
    });
  }, {
    scope: container
  });
  return <section data-section-id="23128" id="consultationcta" data-nav="light" className="relative bg-background text-foreground py-[clamp(8rem,14vw,13rem)] overflow-hidden">
      {}
      <div aria-hidden="true" className="absolute top-0 left-0 right-0 h-px" style={{
      backgroundColor: "var(--border)"
    }} />

      <div ref={container} className="mx-auto w-full max-w-[1440px] px-6 sm:px-10 lg:px-[4.5rem]">
        <div className="mx-auto max-w-[1180px] flex flex-col items-center text-center">
          {}
          <p className="legacy-kicker font-default text-[15px] font-medium uppercase" style={{
          letterSpacing: "0.16em",
          color: "var(--muted-foreground)"
        }}>
            Start Your Growth
          </p>

          {}
          <div className="legacy-rule mt-6 h-px w-[60px]" style={{
          backgroundColor: "var(--border)"
        }} aria-hidden="true" />

          {}
          <h1 className="mt-[72px] font-serif text-foreground" style={{
          fontSize: "clamp(2.25rem, 4.5vw, 3.75rem)",
          fontWeight: 400,
          letterSpacing: "-0.032em",
          lineHeight: 0.95
        }}>
            <span className="block overflow-hidden">
              <span className="legacy-line inline-block">
                {"Scale your search"}
              </span>
            </span>
            <span className="block overflow-hidden">
              <span className="legacy-line inline-block">
                {"visibility "}
                <span className="relative inline-block italic">
                  {"today"}
                  <span aria-hidden="true" className="pointer-events-none absolute left-[-4%] right-[-4%] bottom-[0.02em] w-[108%]" style={{
                  height: "0.28em"
                }}>
                    <svg className="legacy-marker block h-full w-full" viewBox="0 0 300 40" preserveAspectRatio="none" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M4 26 C 40 14, 90 32, 148 20 S 250 30, 296 18" stroke="var(--accent)" strokeWidth="14" strokeLinecap="round" strokeOpacity="0.55" fill="none" />
                    </svg>
                  </span>
                  <span className="relative">.</span>
                </span>
              </span>
            </span>
          </h1>

          {}
          <p className="legacy-lede mt-14 font-default text-foreground max-w-[620px]" style={{
          fontSize: "clamp(1.25rem, 1.6vw, 1.5rem)",
          lineHeight: 1.55,
          letterSpacing: "-0.005em"
        }}>
            {"Book a free 20-minute SEO audit. No sales pressure, just expert advice on how to grow your organic traffic."}
          </p>

          {}
          <div className="legacy-cta-wrap mt-14 relative flex flex-col sm:flex-row items-center justify-center gap-6 sm:gap-0">
            <Button asChild className="h-[60px] rounded-lg bg-primary px-10 text-primary-foreground hover:bg-primary/90 font-default text-[17px] font-medium shadow-sm">
              <Link to="/ContactPage">Book Free Consultation</Link>
            </Button>

            {}
            <div className="legacy-sticker sm:absolute sm:left-full sm:top-1/2 sm:-translate-y-1/2 sm:ml-6 flex items-center justify-center" style={{
            transform: "rotate(-8deg)"
          }} aria-hidden="false">
              <div className="flex h-[92px] w-[92px] items-center justify-center rounded-lg bg-background text-center" style={{
              border: "1.5px solid var(--accent)"
            }}>
                <span className="font-default text-[13px] font-medium uppercase text-foreground leading-tight px-2" style={{
                letterSpacing: "0.14em"
              }}>
                  {"Direct Access"}
                </span>
              </div>
            </div>
          </div>

          {}
          <div className="legacy-tertiary mt-10">
            <Link to="/ContactPage" className="group relative inline-block font-default text-[17px] text-foreground">
              <span>{"or email me directly \u2192"}</span>
              <span aria-hidden="true" className="pointer-events-none absolute left-0 -bottom-1 h-[2px] w-full origin-left scale-x-0 transition-transform duration-[320ms] ease-out group-hover:scale-x-100" style={{
              backgroundColor: "var(--accent)"
            }} />
            </Link>
          </div>
        </div>
      </div>
    </section>;
}